import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Autocomplete,
  Slider,
  InputAdornment,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Share as SocialIcon,
  Mail as DirectMailIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Preview as PreviewIcon,
  Code as CodeIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  TableChart as TableIcon,
} from '@mui/icons-material';

function CampaignForm({
  campaign, onSave, onCancel, mode = 'create',
}) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'email',
    template: {
      name: '',
      subject: '',
      htmlContent: '',
      textContent: '',
      variables: [],
      category: 'general',
      tags: [],
    },
    targetAudience: {
      criteria: {
        leadStages: [],
        propertyTypes: [],
        priceRanges: [],
        locations: [],
        tags: [],
        lastActivity: {
          type: 'days',
          value: 30,
        },
        customFilters: {},
      },
      estimatedRecipients: 0,
      actualRecipients: 0,
    },
    schedule: {
      type: 'immediate',
      scheduledAt: null,
      timezone: 'UTC',
      recurring: {
        frequency: 'daily',
        interval: 1,
        endDate: null,
        daysOfWeek: [],
        dayOfMonth: 1,
      },
    },
    settings: {
      senderName: '',
      senderEmail: '',
      replyToEmail: '',
      trackOpens: true,
      trackClicks: true,
      allowUnsubscribe: true,
      maxSendsPerHour: 100,
      maxSendsPerDay: 1000,
      timezone: 'UTC',
    },
    goals: {
      targetOpenRate: 20,
      targetClickRate: 3,
      targetConversionRate: 1,
      conversionActions: [],
    },
    tags: [],
    category: 'marketing',
  });

  const [leadStages, setLeadStages] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (campaign && mode === 'edit') {
      setFormData(campaign);
    }
    loadReferenceData();
  }, [campaign, mode]);

  const loadReferenceData = async () => {
    try {
      // Load lead stages
      const leadStagesResponse = await fetch('/api/v1/lead-stages', {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });
      if (leadStagesResponse.ok) {
        const leadStagesData = await leadStagesResponse.json();
        setLeadStages(leadStagesData.leadStages || []);
      }

      // Load property types (mock data for now)
      setPropertyTypes(['House', 'Condo', 'Townhouse', 'Apartment', 'Land', 'Commercial', 'Multi-Family']);

      // Load locations (mock data for now)
      setLocations(['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego']);
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (parentField, childField, value) => {
    setFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value,
      },
    }));
  };

  const handleTemplateChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      template: {
        ...prev.template,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await onSave(formData);
    } catch (error) {
      console.error('Error saving campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    {
      value: 'email', label: 'Email Campaign', icon: <EmailIcon />, description: 'Send emails to your contacts',
    },
    {
      value: 'sms', label: 'SMS Campaign', icon: <SmsIcon />, description: 'Send text messages to your contacts',
    },
    {
      value: 'social_media', label: 'Social Media', icon: <SocialIcon />, description: 'Manage social media posts',
    },
    {
      value: 'direct_mail', label: 'Direct Mail', icon: <DirectMailIcon />, description: 'Send physical mail',
    },
    {
      value: 'multi_channel', label: 'Multi-Channel', icon: <CampaignIcon />, description: 'Combine multiple channels',
    },
  ];

  const scheduleOptions = [
    { value: 'immediate', label: 'Send Immediately' },
    { value: 'scheduled', label: 'Schedule for Later' },
    { value: 'recurring', label: 'Recurring Campaign' },
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Form Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Basic Info" />
          <Tab label="Template" />
          <Tab label="Targeting" />
          <Tab label="Schedule" />
          <Tab label="Settings" />
        </Tabs>
      </Box>

      {/* Form Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 0 && (
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Campaign Information
            </Typography>

            <TextField
              label="Campaign Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              fullWidth
              required
              helperText="Give your campaign a descriptive name"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Describe what this campaign is about"
            />

            <FormControl fullWidth>
              <InputLabel>Campaign Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                label="Campaign Type"
              >
                {typeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      {option.icon}
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Tags"
              value={formData.tags.join(', ')}
              onChange={(e) => handleInputChange('tags', e.target.value.split(',').map((tag) => tag.trim()).filter(Boolean))}
              fullWidth
              helperText="Add tags to organize your campaigns (comma-separated)"
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                label="Category"
              >
                <MenuItem value="marketing">Marketing</MenuItem>
                <MenuItem value="sales">Sales</MenuItem>
                <MenuItem value="newsletter">Newsletter</MenuItem>
                <MenuItem value="promotional">Promotional</MenuItem>
                <MenuItem value="educational">Educational</MenuItem>
                <MenuItem value="event">Event</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}

        {activeTab === 1 && (
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Email Template
            </Typography>

            <TextField
              label="Template Name"
              value={formData.template.name}
              onChange={(e) => handleTemplateChange('name', e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Subject Line"
              value={formData.template.subject}
              onChange={(e) => handleTemplateChange('subject', e.target.value)}
              fullWidth
              required
              helperText="This is what recipients will see in their inbox"
            />

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Email Content</Typography>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Preview">
                  <IconButton size="small">
                    <PreviewIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="HTML Editor">
                  <IconButton size="small">
                    <CodeIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              <TextField
                value={formData.template.htmlContent}
                onChange={(e) => handleTemplateChange('htmlContent', e.target.value)}
                fullWidth
                multiline
                rows={12}
                placeholder="Enter your email content here. You can use variables like {{name}}, {{property_address}}, etc."
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            <TextField
              label="Plain Text Version"
              value={formData.template.textContent}
              onChange={(e) => handleTemplateChange('textContent', e.target.value)}
              fullWidth
              multiline
              rows={4}
              helperText="Plain text version for email clients that don't support HTML"
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Available Variables
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {['{{name}}', '{{email}}', '{{phone}}', '{{property_address}}', '{{agent_name}}', '{{company}}'].map((variable) => (
                  <Chip
                    key={variable}
                    label={variable}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      // TODO: Insert variable into content
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        )}

        {activeTab === 2 && (
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Target Audience
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              Define who should receive this campaign. You can target based on lead stages, property preferences, and more.
            </Alert>

            <FormControl fullWidth>
              <InputLabel>Lead Stages</InputLabel>
              <Select
                multiple
                value={formData.targetAudience.criteria.leadStages}
                onChange={(e) => handleNestedChange('targetAudience', 'criteria', {
                  ...formData.targetAudience.criteria,
                  leadStages: e.target.value,
                })}
                label="Lead Stages"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {leadStages.map((stage) => (
                  <MenuItem key={stage._id} value={stage.name}>
                    {stage.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Property Types</InputLabel>
              <Select
                multiple
                value={formData.targetAudience.criteria.propertyTypes}
                onChange={(e) => handleNestedChange('targetAudience', 'criteria', {
                  ...formData.targetAudience.criteria,
                  propertyTypes: e.target.value,
                })}
                label="Property Types"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {propertyTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Locations</InputLabel>
              <Select
                multiple
                value={formData.targetAudience.criteria.locations}
                onChange={(e) => handleNestedChange('targetAudience', 'criteria', {
                  ...formData.targetAudience.criteria,
                  locations: e.target.value,
                })}
                label="Locations"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {locations.map((location) => (
                  <MenuItem key={location} value={location}>
                    {location}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Price Range
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Min Price"
                    type="number"
                    value={formData.targetAudience.criteria.priceRanges[0]?.min || ''}
                    onChange={(e) => {
                      const newRanges = [...formData.targetAudience.criteria.priceRanges];
                      if (newRanges.length === 0) newRanges.push({});
                      newRanges[0].min = parseFloat(e.target.value);
                      handleNestedChange('targetAudience', 'criteria', {
                        ...formData.targetAudience.criteria,
                        priceRanges: newRanges,
                      });
                    }}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Max Price"
                    type="number"
                    value={formData.targetAudience.criteria.priceRanges[0]?.max || ''}
                    onChange={(e) => {
                      const newRanges = [...formData.targetAudience.criteria.priceRanges];
                      if (newRanges.length === 0) newRanges.push({});
                      newRanges[0].max = parseFloat(e.target.value);
                      handleNestedChange('targetAudience', 'criteria', {
                        ...formData.targetAudience.criteria,
                        priceRanges: newRanges,
                      });
                    }}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Last Activity
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Time Unit</InputLabel>
                    <Select
                      value={formData.targetAudience.criteria.lastActivity.type}
                      onChange={(e) => handleNestedChange('targetAudience', 'criteria', {
                        ...formData.targetAudience.criteria,
                        lastActivity: {
                          ...formData.targetAudience.criteria.lastActivity,
                          type: e.target.value,
                        },
                      })}
                      label="Time Unit"
                    >
                      <MenuItem value="days">Days</MenuItem>
                      <MenuItem value="weeks">Weeks</MenuItem>
                      <MenuItem value="months">Months</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={8}>
                  <Slider
                    value={formData.targetAudience.criteria.lastActivity.value}
                    onChange={(_, value) => handleNestedChange('targetAudience', 'criteria', {
                      ...formData.targetAudience.criteria,
                      lastActivity: {
                        ...formData.targetAudience.criteria.lastActivity,
                        value,
                      },
                    })}
                    min={1}
                    max={365}
                    valueLabelDisplay="auto"
                    marks={[
                      { value: 1, label: '1' },
                      { value: 30, label: '30' },
                      { value: 90, label: '90' },
                      { value: 365, label: '365' },
                    ]}
                  />
                </Grid>
              </Grid>
            </Box>

            {formData.targetAudience.estimatedRecipients > 0 && (
              <Alert severity="info">
                Estimated recipients:
                {' '}
                {formData.targetAudience.estimatedRecipients.toLocaleString()}
              </Alert>
            )}
          </Stack>
        )}

        {activeTab === 3 && (
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Campaign Schedule
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Schedule Type</InputLabel>
              <Select
                value={formData.schedule.type}
                onChange={(e) => handleNestedChange('schedule', 'type', e.target.value)}
                label="Schedule Type"
              >
                {scheduleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.schedule.type === 'scheduled' && (
              <TextField
                label="Scheduled Date & Time"
                type="datetime-local"
                value={formData.schedule.scheduledAt || ''}
                onChange={(e) => handleNestedChange('schedule', 'scheduledAt', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}

            {formData.schedule.type === 'recurring' && (
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={formData.schedule.recurring.frequency}
                    onChange={(e) => handleNestedChange('schedule', 'recurring', {
                      ...formData.schedule.recurring,
                      frequency: e.target.value,
                    })}
                    label="Frequency"
                  >
                    {frequencyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Interval"
                  type="number"
                  value={formData.schedule.recurring.interval}
                  onChange={(e) => handleNestedChange('schedule', 'recurring', {
                    ...formData.schedule.recurring,
                    interval: parseInt(e.target.value),
                  })}
                  fullWidth
                  helperText="Send every X days/weeks/months"
                />

                <TextField
                  label="End Date"
                  type="date"
                  value={formData.schedule.recurring.endDate || ''}
                  onChange={(e) => handleNestedChange('schedule', 'recurring', {
                    ...formData.schedule.recurring,
                    endDate: e.target.value,
                  })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )}

            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={formData.schedule.timezone}
                onChange={(e) => handleNestedChange('schedule', 'timezone', e.target.value)}
                label="Timezone"
              >
                <MenuItem value="UTC">UTC</MenuItem>
                <MenuItem value="America/New_York">Eastern Time</MenuItem>
                <MenuItem value="America/Chicago">Central Time</MenuItem>
                <MenuItem value="America/Denver">Mountain Time</MenuItem>
                <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}

        {activeTab === 4 && (
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Campaign Settings
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Sender Name"
                  value={formData.settings.senderName}
                  onChange={(e) => handleNestedChange('settings', 'senderName', e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Sender Email"
                  type="email"
                  value={formData.settings.senderEmail}
                  onChange={(e) => handleNestedChange('settings', 'senderEmail', e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>

            <TextField
              label="Reply-To Email"
              type="email"
              value={formData.settings.replyToEmail}
              onChange={(e) => handleNestedChange('settings', 'replyToEmail', e.target.value)}
              fullWidth
              helperText="Where replies should be sent"
            />

            <Divider />

            <Typography variant="subtitle1" fontWeight={600}>
              Tracking & Analytics
            </Typography>

            <FormControlLabel
              control={(
                <Switch
                  checked={formData.settings.trackOpens}
                  onChange={(e) => handleNestedChange('settings', 'trackOpens', e.target.checked)}
                />
              )}
              label="Track Email Opens"
            />

            <FormControlLabel
              control={(
                <Switch
                  checked={formData.settings.trackClicks}
                  onChange={(e) => handleNestedChange('settings', 'trackClicks', e.target.checked)}
                />
              )}
              label="Track Link Clicks"
            />

            <FormControlLabel
              control={(
                <Switch
                  checked={formData.settings.allowUnsubscribe}
                  onChange={(e) => handleNestedChange('settings', 'allowUnsubscribe', e.target.checked)}
                />
              )}
              label="Allow Unsubscribe"
            />

            <Divider />

            <Typography variant="subtitle1" fontWeight={600}>
              Sending Limits
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Max Sends Per Hour"
                  type="number"
                  value={formData.settings.maxSendsPerHour}
                  onChange={(e) => handleNestedChange('settings', 'maxSendsPerHour', parseInt(e.target.value))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Max Sends Per Day"
                  type="number"
                  value={formData.settings.maxSendsPerDay}
                  onChange={(e) => handleNestedChange('settings', 'maxSendsPerDay', parseInt(e.target.value))}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Divider />

            <Typography variant="subtitle1" fontWeight={600}>
              Campaign Goals
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Target Open Rate (%)"
                  type="number"
                  value={formData.goals.targetOpenRate}
                  onChange={(e) => handleNestedChange('goals', 'targetOpenRate', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Target Click Rate (%)"
                  type="number"
                  value={formData.goals.targetClickRate}
                  onChange={(e) => handleNestedChange('goals', 'targetClickRate', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Target Conversion Rate (%)"
                  type="number"
                  value={formData.goals.targetConversionRate}
                  onChange={(e) => handleNestedChange('goals', 'targetConversionRate', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>
            </Grid>
          </Stack>
        )}
      </Box>

      {/* Form Actions */}
      <Box sx={{
        borderTop: 1, borderColor: 'divider', p: 2, mt: 2,
      }}
      >
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onCancel}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading || !formData.name || !formData.template.subject}
          >
            {loading ? 'Saving...' : (mode === 'create' ? 'Create Campaign' : 'Update Campaign')}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

export default CampaignForm;
